import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';
import { SITE_CONFIG } from './config';

// ============================================
// Hook para animaciones al scroll
// ============================================
export const useScrollAnimation = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold, rootMargin: '50px' }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// ============================================
// Componente de scroll reveal
// ============================================
export const ScrollReveal = ({ children, delay = 0, direction = 'up', className = '' }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  const directions = {
    up: 'translateY(40px)',
    down: 'translateY(-40px)',
    left: 'translateX(40px)',
    right: 'translateX(-40px)',
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) translateX(0)' : directions[direction],
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// ============================================
// Navegación principal
// ============================================
export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/web' || location.pathname === '/web/';

  useEffect(() => {
    // Verificar estado inicial del scroll
    setIsScrolled(window.scrollY > 20);
    
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Inicio', href: '/web' },
    { name: 'Desayunos', href: '/web/desayunos' },
    { name: 'Lunch', href: '/web/lunch' },
    { name: 'Cócteles', href: '/web/cocteles' },
    { name: 'Blog', href: '/web/blog' },
    { name: 'Nosotros', href: '/web/nosotros' },
    { name: 'Contacto', href: '/web/contacto' },
  ];

  // Mostrar versión oscura (logo negro) cuando:
  // 1. Se ha hecho scroll
  // 2. No estamos en la página de inicio (otras páginas tienen fondo claro)
  const showDark = isScrolled || !isHome;

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      showDark
        ? 'bg-white shadow-md' 
        : 'bg-gradient-to-b from-black/50 to-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/web" className="flex-shrink-0">
            <img 
              src={showDark ? "/duetto-logo-negro.png" : "/duetto-logo-blanco.png"}
              alt="Duetto Catering - Catering para empresas en Madrid" 
              className="h-8 lg:h-10 object-contain transition-opacity duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`text-sm font-medium transition-colors duration-300 hover:text-emerald-600 ${
                  showDark ? 'text-gray-700' : 'text-white'
                } ${location.pathname === link.href ? 'text-emerald-600' : ''}`}
              >
                {link.name}
              </Link>
            ))}
            <Link
              to="/web/contacto"
              className="ml-2 px-5 py-2.5 bg-emerald-600 text-white text-sm font-semibold rounded-full hover:bg-emerald-700 transition-all duration-300 hover:shadow-lg"
            >
              Pedir presupuesto
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              showDark ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10'
            }`}
            aria-label="Menú"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden absolute top-full left-0 right-0 bg-white shadow-xl transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
      }`}>
        <div className="px-4 py-6 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors ${
                location.pathname === link.href ? 'bg-emerald-50 text-emerald-600' : ''
              }`}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/web/contacto"
            onClick={() => setIsMobileMenuOpen(false)}
            className="block w-full text-center px-4 py-3 mt-4 bg-emerald-600 text-white font-semibold rounded-full"
          >
            Pedir presupuesto
          </Link>
        </div>
      </div>
    </nav>
  );
};

// ============================================
// Footer
// ============================================
export const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Logo y descripción */}
          <div className="lg:col-span-1">
            <img 
              src="/duetto-logo-blanco.png" 
              alt="Duetto Catering" 
              className="h-10 object-contain mb-4"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              Catering para empresas en Madrid. Desayunos, lunch y cócteles desde 2018.
            </p>
          </div>

          {/* Servicios */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">Servicios</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><Link to="/web/desayunos" className="hover:text-white transition-colors">Desayunos para empresas</Link></li>
              <li><Link to="/web/lunch" className="hover:text-white transition-colors">Lunch y buffet</Link></li>
              <li><Link to="/web/cocteles" className="hover:text-white transition-colors">Cócteles corporativos</Link></li>
            </ul>
          </div>

          {/* Empresa */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">Empresa</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li><Link to="/web/nosotros" className="hover:text-white transition-colors">Sobre nosotros</Link></li>
              <li><Link to="/web/blog" className="hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/web/contacto" className="hover:text-white transition-colors">Contacto</Link></li>
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-4">Contacto</h3>
            <ul className="space-y-3 text-sm text-gray-400">
              <li>
                <a href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors flex items-center gap-2">
                  <Phone size={16} />
                  {SITE_CONFIG.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${SITE_CONFIG.email}`} className="hover:text-white transition-colors">
                  {SITE_CONFIG.email}
                </a>
              </li>
              <li className="text-gray-500">Madrid y alrededores</li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            © {new Date().getFullYear()} Duetto Catering. Todos los derechos reservados.
          </p>
          <div className="flex gap-6 text-sm text-gray-500">
            <a href="#" className="hover:text-white transition-colors">Política de privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Aviso legal</a>
            <a href="#" className="hover:text-white transition-colors">Cookies</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============================================
// Chat Widget (en vivo)
// ============================================
export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: 'bot', text: '¡Hola! 👋 ¿En qué podemos ayudarte?' }
  ]);
  const [input, setInput] = useState('');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });
  const [showForm, setShowForm] = useState(true);

  const handleStartChat = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.email) {
      setShowForm(false);
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: `¡Hola ${userInfo.name}! ¿Qué tipo de evento estás organizando?` 
      }]);
    }
  };

  const handleSend = (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    setMessages(prev => [...prev, { from: 'user', text: input }]);
    setInput('');
    
    // Respuesta automática simple (en el futuro conectará con Gemini)
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: 'Gracias por tu mensaje. Uno de nuestros asesores te responderá en breve. Si prefieres, puedes llamarnos al 91 572 24 41.' 
      }]);
    }, 1000);
  };

  return (
    <>
      {/* Botón flotante */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen ? 'bg-gray-700 rotate-0' : 'bg-emerald-600 hover:bg-emerald-700 hover:scale-110'
        }`}
        aria-label={isOpen ? 'Cerrar chat' : 'Abrir chat'}
      >
        {isOpen ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
      </button>

      {/* Ventana de chat */}
      <div className={`fixed bottom-24 right-6 z-50 w-80 sm:w-96 bg-white rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
        isOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible translate-y-4'
      }`}>
        {/* Header */}
        <div className="bg-emerald-600 text-white p-4">
          <h3 className="font-semibold">Duetto Catering</h3>
          <p className="text-sm text-emerald-100">Te respondemos en minutos</p>
        </div>

        {/* Contenido */}
        <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {showForm ? (
            <form onSubmit={handleStartChat} className="space-y-3">
              <p className="text-sm text-gray-600">Para comenzar, indícanos tu nombre y email:</p>
              <input
                type="text"
                placeholder="Tu nombre"
                value={userInfo.name}
                onChange={(e) => setUserInfo({...userInfo, name: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                required
              />
              <input
                type="email"
                placeholder="Tu email"
                value={userInfo.email}
                onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                required
              />
              <button
                type="submit"
                className="w-full py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                Iniciar chat
              </button>
            </form>
          ) : (
            messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                  msg.from === 'user' 
                    ? 'bg-emerald-600 text-white rounded-br-md' 
                    : 'bg-white text-gray-700 shadow-sm rounded-bl-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Input */}
        {!showForm && (
          <form onSubmit={handleSend} className="p-3 bg-white border-t">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escribe tu mensaje..."
                className="flex-1 px-3 py-2 border rounded-full text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-emerald-600 text-white rounded-full text-sm font-medium hover:bg-emerald-700 transition-colors"
              >
                Enviar
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};

// ============================================
// Layout principal
// ============================================
export const PublicLayout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-grow">
        {children}
      </main>
      <Footer />
      <ChatWidget />
    </div>
  );
};

// ============================================
// Componentes de UI reutilizables
// ============================================
export const SectionTitle = ({ badge, title, subtitle, center = true, light = false }) => (
  <div className={`max-w-3xl ${center ? 'mx-auto text-center' : ''} mb-12 lg:mb-16`}>
    {badge && (
      <span className={`inline-block text-sm font-semibold tracking-wider uppercase mb-3 ${
        light ? 'text-emerald-400' : 'text-emerald-600'
      }`}>
        {badge}
      </span>
    )}
    <h2 className={`text-3xl lg:text-4xl xl:text-5xl font-bold tracking-tight ${
      light ? 'text-white' : 'text-gray-900'
    }`}>
      {title}
    </h2>
    {subtitle && (
      <p className={`mt-4 text-lg lg:text-xl ${light ? 'text-gray-300' : 'text-gray-600'}`}>
        {subtitle}
      </p>
    )}
  </div>
);

export const PriceTag = ({ price, suffix = '/persona' }) => (
  <div className="flex items-baseline gap-1">
    <span className="text-3xl font-bold text-emerald-600">{price}€</span>
    <span className="text-gray-500 text-sm">{suffix}</span>
  </div>
);

export const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    popular: 'bg-emerald-100 text-emerald-700',
    new: 'bg-blue-100 text-blue-700',
  };
  
  return (
    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${variants[variant]}`}>
      {children}
    </span>
  );
};
